import { Component, OnInit, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.css']
})
export class AdminLayoutComponent implements OnInit {
  sidebarOpen = true;
  currentRoute = '';
  user: any = null;
  
  // Touch/swipe gesture properties
  private touchStartX = 0;
  private touchEndX = 0;
  private readonly SWIPE_THRESHOLD = 50; // minimum distance for swipe

  menuItems = [
    { 
      label: 'Dashboard', 
      icon: 'dashboard', 
      route: '/admin/dashboard',
      description: 'Overview & Metrics'
    },
    { 
      label: 'Analytics', 
      icon: 'chart', 
      route: '/admin/analytics',
      description: 'Detailed Reports'
    },
    { 
      label: 'Conversation Log', 
      icon: 'chat', 
      route: '/admin/conversation-log',
      description: 'Message Threads'
    },
    { 
      label: 'AI Knowledgebase', 
      icon: 'database', 
      route: '/admin/knowledgebase',
      description: 'Training Dataset'
    },
    { 
      label: 'FAQs', 
      icon: 'help', 
      route: '/admin/faq-management',
      description: 'Knowledge Base'
    },
    { 
      label: 'Emergency Resources', 
      icon: 'support', 
      route: '/admin/complaints',
      description: 'Crisis Hotlines'
    },
    { 
      label: 'Settings', 
      icon: 'settings', 
      route: '/admin/settings',
      description: 'System Config'
    }
  ];

  constructor(
    private router: Router,
    private location: Location,
    private authService: AuthService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.url;
      }
    });
  }

  ngOnInit() {
    this.user = this.authService.getCurrentUser();
    this.currentRoute = this.router.url;
    
    // Auto-collapse sidebar on mobile
    if (window.innerWidth < 1024) {
      this.sidebarOpen = false;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    // Auto-collapse on mobile resize
    if (event.target.innerWidth < 1024) {
      this.sidebarOpen = false;
    } else {
      this.sidebarOpen = true;
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipeGesture();
  }

  private handleSwipeGesture(): void {
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    // Only handle swipes on mobile
    if (window.innerWidth >= 1024) return;

    // Swipe right (open sidebar) - only from left edge
    if (swipeDistance > this.SWIPE_THRESHOLD && this.touchStartX < 50) {
      this.sidebarOpen = true;
    }
    
    // Swipe left (close sidebar)
    if (swipeDistance < -this.SWIPE_THRESHOLD && this.sidebarOpen) {
      this.sidebarOpen = false;
    }
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  onMenuItemClick() {
    // Close sidebar on mobile when menu item is clicked
    if (window.innerWidth < 1024) {
      this.sidebarOpen = false;
    }
  }

  goBack() {
    this.location.back();
  }

  isActive(route: string): boolean {
    return this.currentRoute === route || this.currentRoute.startsWith(route + '/');
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  getPageTitle(): string {
    const activeItem = this.menuItems.find(item => this.isActive(item.route));
    return activeItem ? activeItem.label : 'Admin Panel';
  }
}
